import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { NetblitzLogo } from '@/components/brand/Logo';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/config/routes';

// Customer-focused navigation labels
const navLabels = {
  nl: {
    websiteMaken: 'Website laten maken',
    pakketten: 'Pakketten & Prijzen',
    realisaties: 'Realisaties',
    blog: 'Blog',
    faq: 'FAQ',
    advies: 'Gratis Advies',
    cta: 'Offerte aanvragen'
  },
  en: {
    websiteMaken: 'Get a Website',
    pakketten: 'Packages & Pricing',
    realisaties: 'Our Work',
    blog: 'Blog',
    faq: 'FAQ',
    advies: 'Free Consultation',
    cta: 'Request Quote'
  },
  fr: {
    websiteMaken: 'Créer un site',
    pakketten: 'Forfaits & Tarifs',
    realisaties: 'Réalisations',
    blog: 'Blog',
    faq: 'FAQ',
    advies: 'Conseil Gratuit',
    cta: 'Demander un devis'
  },
  de: {
    websiteMaken: 'Website erstellen',
    pakketten: 'Pakete & Preise',
    realisaties: 'Referenzen',
    blog: 'Blog',
    faq: 'FAQ',
    advies: 'Gratis Beratung',
    cta: 'Angebot anfordern'
  }
};

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();
  
  const labels = navLabels[language] || navLabels.nl;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Customer-focused navigation - simplified menu
  const navLinks = [
    { routeKey: 'services', label: labels.websiteMaken },
    { routeKey: 'pricing', label: labels.pakketten },
    { routeKey: 'portfolio', label: labels.realisaties },
    { routeKey: 'blog', label: labels.blog },
    { routeKey: 'faq', label: labels.faq },
    { routeKey: 'contact', label: labels.advies },
  ].map(link => ({
    ...link,
    path: getLocalizedPath(link.routeKey, language)
  }));

  // Check if current path matches a nav link
  const isActivePath = (path) => {
    const currentPath = location.pathname.replace(/\/$/, '');
    const linkPath = path.replace(/\/$/, '');
    return currentPath === linkPath;
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-[0_2px_20px_-2px_rgba(0,0,0,0.1)] border-b border-gray-200' 
          : 'bg-white/80 backdrop-blur-sm'
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link 
            to={getLocalizedPath('home', language)} 
            className="flex items-center gap-2 group flex-shrink-0"
            data-testid="navbar-logo"
          >
            <NetblitzLogo size={36} className="group-hover:scale-110 transition-transform" />
            <span className="text-xl lg:text-2xl font-bold text-[#111827]">
              Net<span className="text-[#2563EB]">blitz</span>
            </span>
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center gap-1 xl:gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.routeKey}
                to={link.path}
                className={`px-3 xl:px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                  isActivePath(link.path) 
                    ? 'text-[#2563EB] bg-blue-50' 
                    : 'text-[#4B5563] hover:text-[#2563EB] hover:bg-gray-100'
                }`}
                data-testid={`nav-link-${link.routeKey}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side: Language + CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to={getLocalizedPath('contact', language)}
              className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold px-5 py-2.5 rounded-full transition-all duration-200 shadow-md hover:shadow-lg hover:shadow-blue-500/25 group"
              data-testid="navbar-cta"
            >
              {labels.cta}
              <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-[#4B5563] hover:text-[#2563EB] hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="py-4 space-y-1 border-t border-gray-200">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.routeKey}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      className={`block py-3 px-4 text-base font-medium rounded-xl transition-all ${
                        isActivePath(link.path)
                          ? 'text-[#2563EB] bg-blue-50'
                          : 'text-[#111827] hover:text-[#2563EB] hover:bg-gray-100'
                      }`}
                      data-testid={`mobile-nav-link-${link.routeKey}`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navLinks.length * 0.05 }}
                  className="pt-3 px-4"
                >
                  <Link
                    to={getLocalizedPath('contact', language)}
                    className="flex items-center justify-center gap-2 w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold py-3 px-6 rounded-full transition-all shadow-md"
                    data-testid="mobile-cta"
                  >
                    {labels.cta}
                    <ArrowRight size={16} />
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </motion.header>
  );
};
